import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { formatPrice } from "@ckgo2/shared/utils";
import { api } from "@/api/client";
import { Ban, Loader2, AlertTriangle } from "lucide-react";

interface Impact {
  currency: string;
  departureDate: string | null;
  daysToDeparture: number | null;
  feePercent: number;
  policyFeePercent: number;
  total: number;
  paidTotal: number;
  stornoFee: number;
  refundAmount: number;
  supplierLoss: number;
  netImpact: number;
  serviceBookings: Array<{ id: string; name: string; supplierDeposit: number }>;
}

/**
 * Storno panel (Block 2 B2.7). Loads the read-only cancellation proposal (tier fee / refund /
 * supplier loss / net impact), lets the garant forgive part of the fee, and confirms — which
 * cancels the order, cancels its service bookings, issues a credit note and emails the customer.
 */
export default function CancellationPanel({
  orderId,
  status,
  cancellationMeta,
}: {
  orderId: string;
  status: string;
  cancellationMeta?: Record<string, unknown> | null;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [waive, setWaive] = useState(0);
  const [reason, setReason] = useState("");

  const isCancelled = status === "cancelled";

  const proposal = useQuery({
    queryKey: ["cancellation", orderId, waive],
    queryFn: () =>
      api.get<{ data: Impact }>(`/api/admin/orders/${orderId}/cancellation?waiveFeePct=${waive}`),
    enabled: open && !isCancelled,
  });

  const cancel = useMutation({
    mutationFn: () =>
      api.post(`/api/admin/orders/${orderId}/cancel`, {
        reason: reason || null,
        waiveFeePct: waive,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["order", orderId] });
      qc.invalidateQueries({ queryKey: ["order-audit", orderId] });
      setOpen(false);
    },
  });

  // Canonical money formatter (shared) — keep every panel's currency rendering identical.
  const m = (n: number, c: string) => formatPrice(n || 0, c);

  if (isCancelled) {
    const meta = cancellationMeta || {};
    const cur = String(meta.currency || "CZK");
    return (
      <div className="card border-red-200">
        <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold text-red-700">
          <Ban className="h-5 w-5" /> {t("orders.cancellation.cancelledTitle")}
        </h2>
        <dl className="space-y-1 text-sm">
          <Row
            label={t("orders.cancellation.stornoFee")}
            value={`${Number(meta.feePercent ?? 0)}% · ${m(Number(meta.stornoFee ?? 0), cur)}`}
          />
          <Row
            label={t("orders.cancellation.refundToCustomer")}
            value={m(Number(meta.refundAmount ?? 0), cur)}
          />
          <Row
            label={t("orders.cancellation.supplierLoss")}
            value={m(Number(meta.supplierLoss ?? 0), cur)}
          />
          <Row
            label={t("orders.cancellation.netImpact")}
            value={m(Number(meta.netImpact ?? 0), cur)}
            strong
          />
          {meta.reason ? (
            <Row label={t("orders.cancellation.reason")} value={String(meta.reason)} />
          ) : null}
        </dl>
      </div>
    );
  }

  const d = proposal.data?.data;

  return (
    <div className="card border-red-200">
      <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-red-700">
        <AlertTriangle className="h-5 w-5" /> {t("orders.cancellation.title")}
      </h2>
      {!open ? (
        <button onClick={() => setOpen(true)} className="btn-ghost btn-sm mt-2 w-full text-red-700">
          <Ban className="h-4 w-4" /> {t("orders.cancellation.start")}
        </button>
      ) : (
        <div className="mt-2 space-y-3">
          {proposal.isLoading || !d ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : (
            <dl className="space-y-1 text-sm">
              <Row
                label={t("orders.cancellation.daysToDeparture")}
                value={d.daysToDeparture == null ? "—" : String(d.daysToDeparture)}
              />
              <Row label={t("orders.cancellation.policyFee")} value={`${d.policyFeePercent}%`} />
              <Row
                label={t("orders.cancellation.effectiveFee")}
                value={`${d.feePercent}% · ${m(d.stornoFee, d.currency)}`}
              />
              <Row label={t("orders.cancellation.paidSoFar")} value={m(d.paidTotal, d.currency)} />
              <Row
                label={t("orders.cancellation.refundDue")}
                value={m(d.refundAmount, d.currency)}
                strong
              />
              {d.supplierLoss > 0 && (
                <Row
                  label={t("orders.cancellation.supplierLoss")}
                  value={m(d.supplierLoss, d.currency)}
                />
              )}
              <Row
                label={t("orders.cancellation.netImpact")}
                value={m(d.netImpact, d.currency)}
                strong
              />
              {d.serviceBookings.length > 0 && (
                <Row
                  label={t("orders.cancellation.servicesToCancel")}
                  value={String(d.serviceBookings.length)}
                />
              )}
            </dl>
          )}

          <label className="block text-xs font-semibold text-gray-600">
            {t("orders.cancellation.waiveFee")}
            <input
              type="number"
              min={0}
              max={100}
              value={waive}
              onChange={(e) => setWaive(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
              className="input mt-1"
            />
          </label>
          <textarea
            className="input"
            rows={2}
            placeholder={t("orders.cancellation.reasonPlaceholder")}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="flex gap-2">
            <button
              onClick={() => cancel.mutate()}
              disabled={cancel.isPending}
              className="btn-sm flex-1 rounded-lg bg-red-600 font-semibold text-white hover:bg-red-700 disabled:opacity-50"
            >
              {cancel.isPending ? (
                <Loader2 className="mx-auto h-4 w-4 animate-spin" />
              ) : (
                t("orders.cancellation.confirm")
              )}
            </button>
            <button onClick={() => setOpen(false)} className="btn-ghost btn-sm">
              {t("common.actions.back")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between">
      <dt className="text-gray-500">{label}</dt>
      <dd
        className={
          strong
            ? "font-semibold text-gray-900 dark:text-white"
            : "text-gray-700 dark:text-gray-300"
        }
      >
        {value}
      </dd>
    </div>
  );
}
